import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Button from '../ui/Button';
import {
  WorkspaceEmptyState,
  WorkspacePageError,
  WorkspacePageLoader,
  WorkspaceStatusChip,
} from '../ui/Workspace';
import { useAdminEnrollmentQueue } from '../../hooks/useLmsData';
import {
  approveEnrollmentRequest,
  assignStudentToBatch,
  declineEnrollmentRequest,
  removeEnrollmentRecord,
} from '../../utils/lmsMutations';
import { useAuth } from '../../contexts/AuthContext';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    borderColor: state.isFocused ? 'var(--color-primary-500, #d61f74)' : '#d6d3d1',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(214,31,116,0.15)' : 'none',
    '&:hover': { borderColor: '#a8a29e' },
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  menu: (base) => ({
    ...base,
    position: 'absolute',
    zIndex: 999,
    borderRadius: '0.5rem',
    boxShadow: '0 4px 16px -4px rgba(0,0,0,0.12)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const EnrollmentManagement = () => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const { currentUser } = useAuth();
  const { data, loading, error, refresh } = useAdminEnrollmentQueue();

  const [selectedBatchKey, setSelectedBatchKey] = useState('');
  const [requestFilter, setRequestFilter] = useState('all');
  const [manualStudentId, setManualStudentId] = useState('');
  const [manualCourseId, setManualCourseId] = useState('');
  const [manualBatchId, setManualBatchId] = useState('');
  const [assigningStudent, setAssigningStudent] = useState(false);
  const [bulkSourceCourseId, setBulkSourceCourseId] = useState('');
  const [bulkSourceBatchId, setBulkSourceBatchId] = useState('');
  const [bulkTargetCourseId, setBulkTargetCourseId] = useState('');
  const [bulkTargetBatchId, setBulkTargetBatchId] = useState('');
  const [bulkSelectedStudentIds, setBulkSelectedStudentIds] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [bulkSectionOpen, setBulkSectionOpen] = useState(false);
  const [enrolledPage, setEnrolledPage] = useState(1);
  const ENROLLED_PAGE_SIZE = 20;

  const courseList = useMemo(() => data?.courseList || [], [data]);
  const students = data?.students || {};
  const enrollments = useMemo(() => data?.enrollments || [], [data?.enrollments]);
  const pendingRequests = useMemo(() => data?.pendingRequests || [], [data]);

  const batchWorkspaces = useMemo(
    () =>
      courseList.flatMap((course) =>
        (course.seatSummary || []).map((batch) => {
          const batchKey = `${course.id}:${batch.batchId || batch.batchIndex}`;
          return {
            batchKey,
            course,
            batch,
            batchRequests: pendingRequests.filter(
              (req) =>
                req.courseId === course.id &&
                String(req.batchName) === String(batch.batchId || req.batchId),
            ),
          };
        }),
      ),
    [courseList, pendingRequests],
  );

  const selectedWorkspace = useMemo(
    () => batchWorkspaces.find((w) => w.batchKey === selectedBatchKey) || batchWorkspaces[0] || null,
    [batchWorkspaces, selectedBatchKey],
  );

  useEffect(() => {
    if (!batchWorkspaces.length) return;
    if (!batchWorkspaces.some((w) => w.batchKey === selectedBatchKey)) {
      setSelectedBatchKey(batchWorkspaces[0].batchKey);
    }
  }, [batchWorkspaces, selectedBatchKey]);

  const studentOptions = Object.entries(students).map(([id, s]) => ({
    value: id,
    label:
      s?.personalDetails?.firstName && s?.personalDetails?.lastName
        ? `${s.personalDetails.firstName} ${s.personalDetails.lastName}`
        : `Student ${id}`,
  }));

  const courseOptions = courseList.map((c) => ({
    value: c.id,
    label: c.name || `Course ${c.id}`,
  }));

  const selectedManualCourse = courseList.find((c) => c.id === manualCourseId);
  const manualBatchOptions = (selectedManualCourse?.seatSummary || []).map((batch) => ({
    value: String(batch.batchId ?? batch.batchIndex),
    label: `${batch.batchId || `Batch ${batch.batchIndex + 1}`} — ${
      resolveBranchRef(batch, { byId: branchesById, bySlug: branchesBySlug })?.name || 'Branch not set'
    } — ${batch.availableSeats} seats left`,
    batch,
  }));
  const selectedManualBatch = manualBatchOptions.find(
    (o) => o.value === String(manualBatchId),
  )?.batch;

  const buildBatchOptions = useCallback(
    (courseId) => {
      const course = courseList.find((c) => c.id === courseId);
      return (course?.seatSummary || []).map((batch) => ({
        value: String(batch.batchId ?? batch.batchIndex),
        label: `${batch.batchId || `Batch ${batch.batchIndex + 1}`} — ${
          resolveBranchRef(batch, { byId: branchesById, bySlug: branchesBySlug })?.name || 'Branch not set'
        } — ${batch.enrolledCount || 0}/${batch.maxStudents || 0} enrolled`,
        batch,
      }));
    },
    [branchesById, branchesBySlug, courseList],
  );

  const bulkSourceBatchOptions = useMemo(
    () => buildBatchOptions(bulkSourceCourseId),
    [bulkSourceCourseId, buildBatchOptions],
  );
  const bulkTargetBatchOptions = useMemo(
    () => buildBatchOptions(bulkTargetCourseId),
    [bulkTargetCourseId, buildBatchOptions],
  );

  const selectedBulkSourceBatch = bulkSourceBatchOptions.find(
    (o) => o.value === String(bulkSourceBatchId),
  )?.batch;
  const selectedBulkTargetBatch = bulkTargetBatchOptions.find(
    (o) => o.value === String(bulkTargetBatchId),
  )?.batch;

  const bulkSourceEnrollments = useMemo(() => {
    if (!bulkSourceCourseId || !bulkSourceBatchId) return [];
    return enrollments.filter(
      (e) =>
        e.courseId === bulkSourceCourseId &&
        String(e.batchId) === String(bulkSourceBatchId) &&
        (!selectedBulkSourceBatch?.branchId ||
          !e.branchId ||
          String(e.branchId) === String(selectedBulkSourceBatch.branchId)) &&
        e.status === 'active',
    );
  }, [bulkSourceBatchId, bulkSourceCourseId, enrollments, selectedBulkSourceBatch]);

  const bulkTargetActiveStudentIds = useMemo(
    () =>
      new Set(
        enrollments
          .filter(
            (e) =>
              e.courseId === bulkTargetCourseId &&
              String(e.batchId) === String(bulkTargetBatchId) &&
              (!selectedBulkTargetBatch?.branchId ||
                !e.branchId ||
                String(e.branchId) === String(selectedBulkTargetBatch.branchId)) &&
              e.status === 'active',
          )
          .map((e) => e.studentId),
      ),
    [bulkTargetBatchId, bulkTargetCourseId, enrollments, selectedBulkTargetBatch],
  );

  const bulkAssignableEnrollments = bulkSourceEnrollments.filter(
    (e) => !bulkTargetActiveStudentIds.has(e.studentId),
  );

  const manualEnrollmentExists = enrollments.some(
    (e) =>
      e.studentId === manualStudentId &&
      e.courseId === manualCourseId &&
      String(e.batchId) === String(manualBatchId) &&
      e.status === 'active',
  );

  const stats = {
    pendingRequests: pendingRequests.length,
    nearCapacity: batchWorkspaces.filter(
      ({ batch }) => batch.maxStudents && batch.availableSeats <= 2,
    ).length,
    openBatches: batchWorkspaces.length,
    fullBatches: batchWorkspaces.filter(({ batch }) => batch.availableSeats === 0).length,
  };

  const filteredPendingRequests = pendingRequests.filter((req) =>
    requestFilter === 'urgent' ? req.availableSeats <= 2 : true,
  );

  const handleStudentAssignment = async (studentId, courseId, batch) => {
    if (!window.confirm('Assign this student to the selected batch?')) return;
    await assignStudentToBatch({
      studentId,
      courseId,
      batchId: batch.batchId ?? batch.batchIndex,
      batchIndex: batch.batchIndex,
      branchId: batch.branchId,
    });
    refresh();
  };

  const handleManualStudentAssignment = async () => {
    if (!manualStudentId || !manualCourseId || !manualBatchId) return;
    if (manualEnrollmentExists) {
      window.alert('This student is already active in the selected course batch.');
      return;
    }
    const msg =
      selectedManualBatch?.availableSeats <= 0
        ? 'This batch is full. Add this student anyway as an admin override?'
        : 'Add this student to the selected course and batch?';
    if (!window.confirm(msg)) return;
    setAssigningStudent(true);
    try {
      await assignStudentToBatch({
        studentId: manualStudentId,
        courseId: manualCourseId,
        batchId: manualBatchId,
        batchIndex: selectedManualBatch?.batchIndex,
        branchId: selectedManualBatch?.branchId,
      });
      setManualStudentId('');
      refresh();
    } finally {
      setAssigningStudent(false);
    }
  };

  const toggleBulkStudent = (studentId) => {
    setBulkSelectedStudentIds((cur) =>
      cur.includes(studentId) ? cur.filter((id) => id !== studentId) : [...cur, studentId],
    );
  };

  const toggleAllBulkStudents = () => {
    const ids = bulkAssignableEnrollments.map((e) => e.studentId);
    setBulkSelectedStudentIds((cur) => (cur.length === ids.length ? [] : ids));
  };

  const handleBulkStudentAssignment = async () => {
    if (!bulkTargetCourseId || !bulkTargetBatchId || !selectedBulkTargetBatch) return;
    const studentIds = bulkSelectedStudentIds.filter(
      (id) => !bulkTargetActiveStudentIds.has(id),
    );
    if (!studentIds.length) return;
    const msg =
      selectedBulkTargetBatch.availableSeats < studentIds.length
        ? `The target batch only has ${selectedBulkTargetBatch.availableSeats} seat(s) left. Enroll ${studentIds.length} students anyway as an admin override?`
        : `Enroll ${studentIds.length} selected student(s) into the target course batch?`;
    if (!window.confirm(msg)) return;
    setBulkAssigning(true);
    try {
      for (const studentId of studentIds) {
        await assignStudentToBatch({
          studentId,
          courseId: bulkTargetCourseId,
          batchId: bulkTargetBatchId,
          batchIndex: selectedBulkTargetBatch.batchIndex,
          branchId: selectedBulkTargetBatch.branchId,
        });
      }
      setBulkSelectedStudentIds([]);
      refresh();
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleStudentRemoval = async (enrollment) => {
    if (!window.confirm('Remove this student from the batch?')) return;
    await removeEnrollmentRecord({
      enrollmentId: enrollment.id,
      studentId: enrollment.studentId,
      courseId: enrollment.courseId,
      batchId: enrollment.batchId,
      batchIndex: enrollment.batchIndex,
      branchId: enrollment.branchId,
    });
    refresh();
  };

  const handleEnrollmentRequest = async (request, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this request?`)) return;
    if (status === 'approved') {
      await approveEnrollmentRequest(request, currentUser);
    } else {
      await declineEnrollmentRequest(request.id, currentUser);
    }
    refresh();
  };

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>Failed to load enrollments.</WorkspacePageError>;

  const branchLabel = selectedWorkspace
    ? resolveBranchRef(selectedWorkspace.batch, { byId: branchesById, bySlug: branchesBySlug })
        ?.name || 'Branch not set'
    : '';

  // Paginate enrolled students
  const enrolledStudents = selectedWorkspace?.batch?.enrolledStudents || [];
  const enrolledTotalPages = Math.max(1, Math.ceil(enrolledStudents.length / ENROLLED_PAGE_SIZE));
  const enrolledCurrentPage = Math.min(enrolledPage, enrolledTotalPages);
  const pagedEnrolledStudents = enrolledStudents.slice(
    (enrolledCurrentPage - 1) * ENROLLED_PAGE_SIZE,
    enrolledCurrentPage * ENROLLED_PAGE_SIZE,
  );

  return (
    <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
      {/* Page header */}
      <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              Workspace
            </p>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
              Enrollment Management
            </h1>
          </div>
          {batchWorkspaces.length > 0 && (
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-end xl:max-w-lg xl:justify-end">
              <EnrollField label="Batch workspace">
                <select
                  value={selectedWorkspace?.batchKey || ''}
                  onChange={(e) => setSelectedBatchKey(e.target.value)}
                  className="ledger-input min-h-[38px]"
                >
                  {batchWorkspaces.map((w) => (
                    <option key={w.batchKey} value={w.batchKey}>
                      {w.course.name} · {w.batch.batchId || `Batch ${w.batch.batchIndex + 1}`} · {w.batch.availableSeats} seats
                    </option>
                  ))}
                </select>
              </EnrollField>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
          <EnrollStat
            label="Pending Requests"
            value={stats.pendingRequests}
            tone={stats.pendingRequests ? 'warning' : 'neutral'}
          />
          <EnrollStat
            label="Near Capacity"
            value={stats.nearCapacity}
            tone={stats.nearCapacity ? 'warning' : 'neutral'}
          />
          <EnrollStat label="Open Batches" value={stats.openBatches} />
          <EnrollStat
            label="Full Batches"
            value={stats.fullBatches}
            tone={stats.fullBatches ? 'danger' : 'neutral'}
          />
        </div>

        {/* Batch detail strip */}
        {selectedWorkspace && (
          <div className="mt-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs">
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_1fr_auto] md:items-end">
              <SummaryItem
                label="Course"
                value={selectedWorkspace.course.name || '—'}
              />
              <SummaryItem
                label="Batch"
                value={selectedWorkspace.batch.batchId || `Batch ${selectedWorkspace.batch.batchIndex + 1}`}
              />
              <SummaryItem label="Branch" value={branchLabel} />
              <SummaryItem
                label="Enrolled"
                value={`${selectedWorkspace.batch.enrolledCount || 0} / ${selectedWorkspace.batch.maxStudents || '—'}`}
              />
              <WorkspaceStatusChip
                status={
                  selectedWorkspace.batch.availableSeats === 0
                    ? 'blocked'
                    : selectedWorkspace.batch.availableSeats <= 2
                    ? 'urgent'
                    : 'healthy'
                }
                label={`${selectedWorkspace.batch.availableSeats} seats left`}
              />
            </div>
          </div>
        )}
      </header>

      {/* ── Add Student (collapsed toggle) ─────────────────────────── */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <button
          type="button"
          onClick={() => setAddSectionOpen((v) => !v)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        >
          <div>
            <h2 className="text-base font-semibold tracking-tight text-stone-950">
              Add Student to Course
            </h2>
            <p className="text-xs text-stone-500">
              Place a student directly into a course batch without a pending request.
            </p>
          </div>
          <span className="ml-3 shrink-0 text-xs font-semibold text-stone-500">
            {addSectionOpen ? '▲ Hide' : '▼ Show'}
          </span>
        </button>
        {addSectionOpen && (
          <div className="border-t border-stone-100 px-3 py-3">
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
              <EnrollField label="Student">
                <Select
                  options={studentOptions}
                  value={studentOptions.find((o) => o.value === manualStudentId) || null}
                  onChange={(opt) => setManualStudentId(opt?.value || '')}
                  placeholder="Search student…"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </EnrollField>
              <EnrollField label="Course">
                <Select
                  options={courseOptions}
                  value={courseOptions.find((o) => o.value === manualCourseId) || null}
                  onChange={(opt) => {
                    setManualCourseId(opt?.value || '');
                    setManualBatchId('');
                  }}
                  placeholder="Select course…"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </EnrollField>
              <EnrollField label="Batch">
                <Select
                  options={manualBatchOptions}
                  value={manualBatchOptions.find((o) => o.value === String(manualBatchId)) || null}
                  onChange={(opt) => setManualBatchId(opt?.value || '')}
                  placeholder={manualCourseId ? 'Select batch…' : 'Select course first'}
                  isDisabled={!manualCourseId}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </EnrollField>
              <Button
                onClick={handleManualStudentAssignment}
                disabled={
                  assigningStudent ||
                  !manualStudentId ||
                  !manualCourseId ||
                  !manualBatchId ||
                  manualEnrollmentExists
                }
              >
                {assigningStudent ? 'Adding…' : 'Add Student'}
              </Button>
            </div>
            {manualEnrollmentExists && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                This student is already active in the selected course batch.
              </p>
            )}
            {!manualEnrollmentExists && selectedManualBatch?.availableSeats <= 0 && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                This batch is full. Admin override is allowed and will add the student beyond capacity.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ── Bulk Move (collapsed toggle) ───────────────────────────── */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <button
          type="button"
          onClick={() => setBulkSectionOpen((v) => !v)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        >
          <div>
            <h2 className="text-base font-semibold tracking-tight text-stone-950">
              Bulk Move Students
            </h2>
            <p className="text-xs text-stone-500">
              Select students from a source batch and enroll them into a new batch together.
            </p>
          </div>
          <span className="ml-3 shrink-0 text-xs font-semibold text-stone-500">
            {bulkSectionOpen ? '▲ Hide' : '▼ Show'}
          </span>
        </button>
        {bulkSectionOpen && (
          <div className="border-t border-stone-100">
            <div className="grid grid-cols-1 divide-stone-100 sm:grid-cols-2 sm:divide-x">
              <div className="px-3 py-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500">From batch</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select
                    options={courseOptions}
                    value={courseOptions.find((o) => o.value === bulkSourceCourseId) || null}
                    onChange={(opt) => {
                      setBulkSourceCourseId(opt?.value || '');
                      setBulkSourceBatchId('');
                      setBulkSelectedStudentIds([]);
                    }}
                    placeholder="Source course…"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                  <Select
                    options={bulkSourceBatchOptions}
                    value={bulkSourceBatchOptions.find((o) => o.value === String(bulkSourceBatchId)) || null}
                    onChange={(opt) => {
                      setBulkSourceBatchId(opt?.value || '');
                      setBulkSelectedStudentIds([]);
                    }}
                    placeholder={bulkSourceCourseId ? 'Source batch…' : 'Select course first'}
                    isDisabled={!bulkSourceCourseId}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                </div>
              </div>
              <div className="border-t border-stone-100 px-3 py-3 sm:border-t-0">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500">Into batch</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select
                    options={courseOptions}
                    value={courseOptions.find((o) => o.value === bulkTargetCourseId) || null}
                    onChange={(opt) => {
                      setBulkTargetCourseId(opt?.value || '');
                      setBulkTargetBatchId('');
                    }}
                    placeholder="Target course…"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                  <Select
                    options={bulkTargetBatchOptions}
                    value={bulkTargetBatchOptions.find((o) => o.value === String(bulkTargetBatchId)) || null}
                    onChange={(opt) => setBulkTargetBatchId(opt?.value || '')}
                    placeholder={bulkTargetCourseId ? 'Target batch…' : 'Select course first'}
                    isDisabled={!bulkTargetCourseId}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-stone-200">
              <div className="flex items-center justify-between border-b border-stone-100 bg-stone-50 px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-stone-950">
                    {bulkSourceEnrollments.length} source student{bulkSourceEnrollments.length !== 1 ? 's' : ''}
                  </p>
                  {bulkSourceEnrollments.length > 0 && (
                    <p className="text-xs text-stone-500">
                      {bulkSourceEnrollments.length - bulkAssignableEnrollments.length} already in target
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={toggleAllBulkStudents} disabled={!bulkAssignableEnrollments.length}>
                    {bulkSelectedStudentIds.length === bulkAssignableEnrollments.length ? 'Clear All' : 'Select All'}
                  </Button>
                  <Button type="button" size="sm" onClick={handleBulkStudentAssignment} disabled={bulkAssigning || !bulkSelectedStudentIds.length || !bulkTargetCourseId || !bulkTargetBatchId}>
                    {bulkAssigning ? 'Enrolling…' : `Enroll ${bulkSelectedStudentIds.length || ''}`.trim()}
                  </Button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {!bulkSourceCourseId || !bulkSourceBatchId ? (
                  <div className="p-3"><WorkspaceEmptyState title="Choose a source" message="Select the completed course and batch to list students." /></div>
                ) : bulkSourceEnrollments.length === 0 ? (
                  <div className="p-3"><WorkspaceEmptyState title="No active students" message="There are no active enrollments in the selected source batch." /></div>
                ) : (
                  <table className="min-w-full border-separate border-spacing-0 text-sm">
                    <tbody>
                      {bulkSourceEnrollments.map((enrollment) => {
                        const s = students[enrollment.studentId];
                        const name = s?.personalDetails?.firstName
                          ? `${s.personalDetails.firstName} ${s.personalDetails.lastName || ''}`
                          : `Student ${enrollment.studentId}`;
                        const alreadyInTarget = bulkTargetActiveStudentIds.has(enrollment.studentId);
                        const isSelected = bulkSelectedStudentIds.includes(enrollment.studentId);
                        return (
                          <tr key={enrollment.id} className={alreadyInTarget ? 'opacity-50' : isSelected ? 'bg-stone-50' : ''}>
                            <td className="w-8 border-b border-stone-100 px-3 py-2">
                              <input type="checkbox" checked={isSelected} onChange={() => toggleBulkStudent(enrollment.studentId)} disabled={alreadyInTarget} className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500" />
                            </td>
                            <td className="border-b border-stone-100 px-3 py-2 font-semibold text-stone-950">{name}</td>
                            <td className="border-b border-stone-100 px-3 py-2 text-right">
                              {alreadyInTarget && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">Already enrolled</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Approval Queue — only shown when there are requests ──── */}
      {filteredPendingRequests.length > 0 && (
      <section className="rounded-lg border border-stone-200 bg-white">
        <div className="flex flex-col gap-2 border-b border-stone-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-stone-950">
              Approval Queue
            </h2>
            <p className="text-xs text-stone-500">
              Review pending enrollment requests across all batches.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <EnrollField label="Filter">
              <select
                value={requestFilter}
                onChange={(e) => setRequestFilter(e.target.value)}
                className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
              >
                <option value="all">All requests</option>
                <option value="urgent">Urgent only (≤ 2 seats)</option>
              </select>
            </EnrollField>
          </div>
        </div>
        <div className="p-2">
          <div className="overflow-x-auto rounded-md border border-stone-200">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  {['Student', 'Course', 'Batch', 'Branch', 'Seats', 'Requested', ''].map((col) => (
                    <th key={col} className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPendingRequests.map((req) => (
                  <tr key={req.id} className="group">
                    <td className="border-b border-stone-100 px-3 py-2 font-semibold text-stone-950 group-hover:bg-stone-50">{req.studentName}</td>
                    <td className="border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">{req.courseName}</td>
                    <td className="border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50">
                      <WorkspaceStatusChip status={req.availableSeats <= 2 ? 'urgent' : 'pending'} label={req.batchName} />
                    </td>
                    <td className="border-b border-stone-100 px-3 py-2 text-stone-600 group-hover:bg-stone-50">{req.branch}</td>
                    <td className="border-b border-stone-100 px-3 py-2 text-stone-600 group-hover:bg-stone-50">{req.availableSeats} / {req.maxStudents}</td>
                    <td className="border-b border-stone-100 px-3 py-2 text-stone-500 group-hover:bg-stone-50">{formatDateString(req.requestDate)}</td>
                    <td className="border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleEnrollmentRequest(req, 'approved')} disabled={req.availableSeats <= 0} className="rounded border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40">Approve</button>
                        <button type="button" onClick={() => handleEnrollmentRequest(req, 'declined')} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700">Decline</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      )}

      {/* ── Enrolled Students ──────────────────────────────────────── */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <div className="flex flex-col gap-2 border-b border-stone-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-stone-950">Enrolled Students</h2>
            <p className="text-xs text-stone-500">Current roster for the selected batch. Use quick assign to add without a request.</p>
          </div>
          {selectedWorkspace && (
            <WorkspaceStatusChip
              status={enrolledStudents.length ? 'active' : 'inactive'}
              label={`${enrolledStudents.length} students`}
            />
          )}
        </div>
        {!selectedWorkspace ? (
          <div className="p-3"><WorkspaceEmptyState title="No batches found" message="Create a course batch to manage enrollments." /></div>
        ) : (
          <>
            <div className="grid gap-2 border-b border-stone-100 bg-stone-50 px-3 py-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <EnrollField label="Quick assign — search and select a student to add directly">
                <Select
                  options={studentOptions}
                  onChange={(opt) => opt && handleStudentAssignment(opt.value, selectedWorkspace.course.id, selectedWorkspace.batch)}
                  placeholder="Search student by name…"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </EnrollField>
            </div>
            {selectedWorkspace.batch.availableSeats === 0 && (
              <p className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                This batch is full. Admin override will add students beyond capacity.
              </p>
            )}
            {enrolledStudents.length === 0 ? (
              <div className="p-3"><WorkspaceEmptyState title="No enrolled students" message="Approve a request or use quick assign to add students." /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        {['Student', 'Enrolled', ''].map((col) => (
                          <th key={col} className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEnrolledStudents.map((enrollment) => {
                        const s = students[enrollment.studentId];
                        const name = s?.personalDetails?.firstName
                          ? `${s.personalDetails.firstName} ${s.personalDetails.lastName || ''}`
                          : `Student ${enrollment.studentId}`;
                        return (
                          <tr key={enrollment.id} className="group">
                            <td className="border-b border-stone-100 px-3 py-2 font-semibold text-stone-950 group-hover:bg-stone-50">{name}</td>
                            <td className="border-b border-stone-100 px-3 py-2 text-stone-500 group-hover:bg-stone-50">{formatDateString(enrollment.enrollmentDate)}</td>
                            <td className="border-b border-stone-100 px-3 py-2 text-right group-hover:bg-stone-50">
                              <button type="button" onClick={() => handleStudentRemoval(enrollment)} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700">Remove</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {enrolledTotalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-stone-100 px-3 py-2">
                    <p className="text-xs text-stone-500">
                      Page {enrolledCurrentPage} of {enrolledTotalPages} · {enrolledStudents.length} students
                    </p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEnrolledPage((p) => Math.max(1, p - 1))} disabled={enrolledCurrentPage <= 1} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 disabled:opacity-40">Previous</button>
                      <button type="button" onClick={() => setEnrolledPage((p) => Math.min(enrolledTotalPages, p + 1))} disabled={enrolledCurrentPage >= enrolledTotalPages} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 disabled:opacity-40">Next</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>

      {/* ── Bulk Move Students ────────────────────────────────────── */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-3 py-2">
          <h2 className="text-base font-semibold tracking-tight text-stone-950">
            Bulk Move Students
          </h2>
          <p className="text-xs text-stone-500">
            Filter students from one course batch, select who should continue, then enroll them into a new batch together.
          </p>
        </div>

        <div className="grid grid-cols-1 divide-stone-100 sm:grid-cols-2 sm:divide-x">
          <div className="px-3 py-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              From batch
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                options={courseOptions}
                value={courseOptions.find((o) => o.value === bulkSourceCourseId) || null}
                onChange={(opt) => {
                  setBulkSourceCourseId(opt?.value || '');
                  setBulkSourceBatchId('');
                  setBulkSelectedStudentIds([]);
                }}
                placeholder="Source course…"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
              <Select
                options={bulkSourceBatchOptions}
                value={bulkSourceBatchOptions.find((o) => o.value === String(bulkSourceBatchId)) || null}
                onChange={(opt) => {
                  setBulkSourceBatchId(opt?.value || '');
                  setBulkSelectedStudentIds([]);
                }}
                placeholder={bulkSourceCourseId ? 'Source batch…' : 'Select course first'}
                isDisabled={!bulkSourceCourseId}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>
          </div>

          <div className="border-t border-stone-100 px-3 py-3 sm:border-t-0">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Into batch
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                options={courseOptions}
                value={courseOptions.find((o) => o.value === bulkTargetCourseId) || null}
                onChange={(opt) => {
                  setBulkTargetCourseId(opt?.value || '');
                  setBulkTargetBatchId('');
                }}
                placeholder="Target course…"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
              <Select
                options={bulkTargetBatchOptions}
                value={bulkTargetBatchOptions.find((o) => o.value === String(bulkTargetBatchId)) || null}
                onChange={(opt) => setBulkTargetBatchId(opt?.value || '')}
                placeholder={bulkTargetCourseId ? 'Target batch…' : 'Select course first'}
                isDisabled={!bulkTargetCourseId}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>
          </div>
        </div>

        {/* Student selection list */}
        <div className="border-t border-stone-200">
          <div className="flex items-center justify-between border-b border-stone-100 bg-stone-50 px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-stone-950">
                {bulkSourceEnrollments.length} source student
                {bulkSourceEnrollments.length !== 1 ? 's' : ''}
              </p>
              {bulkSourceEnrollments.length > 0 && (
                <p className="text-xs text-stone-500">
                  {bulkSourceEnrollments.length - bulkAssignableEnrollments.length} already in target
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={toggleAllBulkStudents}
                disabled={!bulkAssignableEnrollments.length}
              >
                {bulkSelectedStudentIds.length === bulkAssignableEnrollments.length
                  ? 'Clear All'
                  : 'Select All'}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleBulkStudentAssignment}
                disabled={
                  bulkAssigning ||
                  !bulkSelectedStudentIds.length ||
                  !bulkTargetCourseId ||
                  !bulkTargetBatchId
                }
              >
                {bulkAssigning
                  ? 'Enrolling…'
                  : `Enroll ${bulkSelectedStudentIds.length || ''}`.trim()}
              </Button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {!bulkSourceCourseId || !bulkSourceBatchId ? (
              <div className="p-3">
                <WorkspaceEmptyState
                  title="Choose a source"
                  message="Select the completed course and batch to list students."
                />
              </div>
            ) : bulkSourceEnrollments.length === 0 ? (
              <div className="p-3">
                <WorkspaceEmptyState
                  title="No active students"
                  message="There are no active enrollments in the selected source batch."
                />
              </div>
            ) : (
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <tbody>
                  {bulkSourceEnrollments.map((enrollment) => {
                    const s = students[enrollment.studentId];
                    const name = s?.personalDetails?.firstName
                      ? `${s.personalDetails.firstName} ${s.personalDetails.lastName || ''}`
                      : `Student ${enrollment.studentId}`;
                    const alreadyInTarget = bulkTargetActiveStudentIds.has(enrollment.studentId);
                    const isSelected = bulkSelectedStudentIds.includes(enrollment.studentId);
                    return (
                      <tr
                        key={enrollment.id}
                        className={alreadyInTarget ? 'opacity-50' : isSelected ? 'bg-stone-50' : ''}
                      >
                        <td className="border-b border-stone-100 px-3 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBulkStudent(enrollment.studentId)}
                            disabled={alreadyInTarget}
                            className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                          />
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2 font-semibold text-stone-950">
                          {name}
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2 text-right">
                          {alreadyInTarget && (
                            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">
                              Already enrolled
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ── Shared sub-components ───────────────────────────────────────── */

const EnrollField = ({ label, children }) => (
  <label className="block min-w-0">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    {children}
  </label>
);

const EnrollStat = ({ label, value, tone = 'neutral' }) => {
  const toneClass =
    tone === 'warning'
      ? 'text-amber-700'
      : tone === 'danger'
      ? 'text-red-700'
      : 'text-stone-950';
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
      <p className={`mt-0.5 truncate text-base font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
};

const SummaryItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate font-semibold text-stone-900">{value}</p>
  </div>
);

const formatDateString = (value) =>
  value ? new Date(value).toLocaleDateString() : 'Not set';

export default EnrollmentManagement;
